"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { authClient } from "@/lib/auth-client";

// iOS Safari 전용 "홈 화면에 추가" 안내 (안드로이드/PC·이미 설치됨·키오스크 제외).
// iOS 는 프로그램으로 설치를 띄울 수 없어(애플 정책) 사용자가 직접 공유→홈 화면에 추가 해야 함.
//
// 어디서/어떻게 띄우나:
//  · /control (컨트롤러=관장 폰) : 진입 후 자동 '바텀시트'. 매 수업 쓰는 반복 사용자라 홈추가 가치가 크고,
//    iOS 는 홈추가 시 '현재 URL' 을 저장하므로 여기서 추가하면 홈 아이콘이 곧장 제어화면으로 열린다.
//    → <IosInstallGuide/> (app/layout.tsx 에 전역 마운트되어 있고, /control 에서만 뜬다)
//  · /player (디스플레이=아이패드 등) : 자동 팝업 안 씀. 페어링 QR 을 가리면(관장이 폰으로 스캔) 안 되고,
//    페어링 후엔 수업 영상을 덮으면 안 되기 때문. 대신 페어링(QR) 화면에 '탭하면 단계 안내가 열리는'
//    인라인 힌트만 둔다. → <PlayerInstallHint/> (app/player/page.tsx 의 페어링 화면에서 렌더)

// 설치 안내 '닫음' 기록은 표면별로 분리 — 컨트롤러와 플레이어는 서로 다른 홈 화면 바로가기(다른 URL)라
// 한쪽을 닫아도 다른 쪽은 독립적으로 안내돼야 한다. (같은 기기로 둘 다 볼 때 서로 안 막게)
const DISMISS_KEY_CONTROL = "ios-install-dismissed-control";
const DISMISS_KEY_PLAYER = "ios-install-dismissed-player";
const DISMISS_DAYS = 14; // "괜찮아요"(거절)·배경 탭 → 14일 뒤 다시 안내
const DISMISS_DAYS_DONE = 365; // "확인했어요"(설치 단계까지 진행) → 설치한 걸로 보고 사실상 영구 억제

type Mode = "hidden" | "card" | "steps";

// 닫음 기록 읽기 — 값이 "<ts>:done"(설치 진행)이면 장기(365일), "<ts>"(거절)이면 14일 창.
export function dismissedRecently(key: string): boolean {
  try {
    const [tsStr, mark] = (localStorage.getItem(key) || "").split(":");
    const ts = Number(tsStr) || 0;
    const days = mark === "done" ? DISMISS_DAYS_DONE : DISMISS_DAYS;
    return !!ts && Date.now() - ts < days * 864e5;
  } catch {
    return false; // localStorage 차단 환경 → 억제 안 함
  }
}

// 닫음 기록 저장 — done=true(설치 단계까지 진행)면 장기 억제로 마킹.
export function rememberDismiss(key: string, done: boolean) {
  try {
    localStorage.setItem(key, done ? `${Date.now()}:done` : String(Date.now()));
  } catch {
    /* localStorage 차단 환경 무시 */
  }
}

// iOS Safari 에서 홈추가가 의미 있는 상태인지 + iOS26+ 공유버튼 위치 판별.
// eligible=false 면 안내를 아예 띄우지 않는다(안드로이드/데스크톱/이미 설치됨/인앱 브라우저).
export function detectIosInstall(): { eligible: boolean; shareInMore: boolean } {
  if (typeof window === "undefined") return { eligible: false, shareInMore: false };

  const ua = window.navigator.userAgent || "";
  const isIOS =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ 는 Mac 으로 위장 → 터치 포인트로 판별
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (!isIOS) return { eligible: false, shareInMore: false };

  // 이미 홈 화면 앱으로 실행 중이면 안내 불필요
  const standalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches;
  if (standalone) return { eligible: false, shareInMore: false };

  // 카카오톡·인스타·페북 등 인앱 브라우저는 홈 화면 추가가 불가 → 안내 안 함(그냥 웹으로 쓰게 둠)
  const inApp = /kakaotalk|fban|fbav|instagram|line\/|naver|daumapps|everytimeapp/i.test(ua);
  if (inApp) return { eligible: false, shareInMore: false };

  // iOS 26+ 부터 사파리 하단바 개편(리퀴드 글래스)으로 공유가 더보기(···) 안으로 들어감 → 단계 분기용.
  // ⚠️ iOS 26 은 UA 의 "CPU iPhone OS" 토큰을 18_7 로 고정(freeze)하고 실제 버전은 "Version/26.x" 로만 노출.
  //    → OS 토큰(18)이 아니라 Safari 버전(Version/)으로 판별해야 한다. 둘 중 큰 값을 써서 구형/신형 모두 안전.
  const versionMatch = ua.match(/Version\/(\d+)/); // 사파리(=iOS) 메이저: iOS 26 → 26
  const osMatch = ua.match(/OS (\d+)_/); // 폴백(구형 iOS): "OS 17_4" → 17
  const iosMajor = Math.max(
    versionMatch ? parseInt(versionMatch[1], 10) : 0,
    osMatch ? parseInt(osMatch[1], 10) : 0,
  );
  const shareInMore = /iphone|ipod/i.test(ua) && iosMajor >= 26;
  return { eligible: true, shareInMore };
}

// 컨트롤러(/control) 자동 안내 — 진입 1.2초 뒤 바텀시트. 한 번 닫으면 14일간 안 뜸.
export function IosInstallGuide() {
  const [mode, setMode] = useState<Mode>("hidden");
  const [shareInMore, setShareInMore] = useState(false);
  const pathname = usePathname();
  const { data: session } = authClient.useSession();

  useEffect(() => {
    const path = pathname || window.location.pathname;
    // 컨트롤러(관장 폰)에서 '로그인 후' 에만 자동으로 띄운다. 디스플레이(/player)는 PlayerInstallHint(인라인)로 처리.
    if (!(path === "/control" || path.startsWith("/control/"))) return;
    if (!session) return; // 로그인 전(로그인 안내 화면 등)엔 미노출

    const { eligible, shareInMore: more } = detectIosInstall();
    if (!eligible) return;

    // 최근에 닫았으면 다시 안 띄움 (컨트롤러 전용 키; 설치 진행자는 장기 억제)
    if (dismissedRecently(DISMISS_KEY_CONTROL)) return;

    // setState 는 effect 동기 호출을 피해 타이머 콜백에서 (set-state-in-effect 회피)
    const t = setTimeout(() => {
      setShareInMore(more);
      setMode("card");
    }, 1200);
    return () => clearTimeout(t);
  }, [pathname, session]);

  if (mode === "hidden") return null;

  const dismiss = () => {
    // 단계 안내(steps)에서 닫음 = 설치까지 진행 → 장기 억제. 카드에서 "괜찮아요" = 거절 → 14일.
    rememberDismiss(DISMISS_KEY_CONTROL, mode === "steps");
    setMode("hidden");
  };

  const dark = isDarkTheme();
  return (
    <InstallSheet dark={dark} onClose={dismiss}>
      {mode === "card" ? (
        <Card dark={dark} onAdd={() => setMode("steps")} onClose={dismiss} />
      ) : (
        <Steps dark={dark} onClose={dismiss} shareInMore={shareInMore} />
      )}
    </InstallSheet>
  );
}

// 디스플레이(/player 페어링 화면) 홈추가 안내 — 아이패드 등을 디스플레이로 쓸 때.
// (1) 진입 후 자동 바텀시트(1.2초, 한 번 닫으면 14일 미노출) + (2) 하단에 항상 있는 인라인 힌트 알약(탭하면 재열람).
// 둘 다 PairingScreen 안에만 있어 페어링(토큰)되면 화면째 사라짐 → 재생 중 영상은 안 가린다.
export function PlayerInstallHint() {
  const t = useTranslations("components.iosGuide");
  const [mode, setMode] = useState<Mode>("hidden");
  const [info, setInfo] = useState<{ eligible: boolean; shareInMore: boolean }>({
    eligible: false,
    shareInMore: false,
  });

  useEffect(() => {
    const d = detectIosInstall();
    setInfo(d);
    if (!d.eligible) return;
    // 자동 팝업만 억제(수동 힌트 알약은 항상 유지, 플레이어 전용 키; 설치 진행자는 장기 억제).
    if (dismissedRecently(DISMISS_KEY_PLAYER)) return;
    // setState 는 effect 동기 호출을 피해 타이머 콜백에서 (set-state-in-effect 회피)
    const t = setTimeout(() => setMode("card"), 1200);
    return () => clearTimeout(t);
  }, []);

  if (!info.eligible) return null;

  const dismiss = () => {
    // 단계 안내(steps)에서 닫음 = 설치까지 진행 → 장기 억제. 카드에서 "괜찮아요" = 거절 → 14일.
    rememberDismiss(DISMISS_KEY_PLAYER, mode === "steps");
    setMode("hidden");
  };

  return (
    <>
      <button
        onClick={() => setMode("steps")}
        style={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "calc(16px + env(safe-area-inset-bottom))",
          zIndex: 60,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          maxWidth: "calc(100vw - 32px)",
          padding: "10px 16px",
          borderRadius: 999,
          border: "1px solid #C9D6EF",
          background: "rgba(255,255,255,.92)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          boxShadow: "0 8px 24px rgba(30,50,110,.14)",
          color: "#3A5BC8",
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <InfoGlyph />
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {t("banner")}
        </span>
        <span style={{ opacity: 0.5 }}>›</span>
      </button>
      {mode !== "hidden" && (
        <InstallSheet dark={isDarkTheme()} onClose={dismiss}>
          {mode === "card" ? (
            <Card dark={isDarkTheme()} onAdd={() => setMode("steps")} onClose={dismiss} />
          ) : (
            <Steps dark={isDarkTheme()} onClose={dismiss} shareInMore={info.shareInMore} />
          )}
        </InstallSheet>
      )}
    </>
  );
}

// 시트도 앱 테마(컨트롤러 라이트 기본)에 맞춘다. tedu-theme 미설정=라이트, "dark"만 다크.
// (시트는 app/layout.tsx 에서 .tedu-root 밖에 마운트돼 CSS 변수를 못 받으므로 색을 직접 분기한다.)
export function isDarkTheme(): boolean {
  try {
    return localStorage.getItem("tedu-theme") === "dark";
  } catch {
    return false;
  }
}
export function palette(dark: boolean) {
  return dark
    ? {
        bg: "#1c1c1e",
        border: "rgba(255,255,255,.08)",
        text: "#fff",
        sub: "rgba(235,235,245,.65)",
        dim: "rgba(235,235,245,.5)",
        step: "rgba(235,235,245,.9)",
        line: "rgba(255,255,255,.15)",
        shadow: "0 20px 60px rgba(0,0,0,.6)",
        scrim: "rgba(0,0,0,.6)",
      }
    : {
        bg: "#ffffff",
        border: "rgba(20,30,60,.10)",
        text: "#17171c",
        sub: "rgba(60,64,74,.66)",
        dim: "rgba(60,64,74,.5)",
        step: "rgba(28,30,38,.92)",
        line: "rgba(20,30,60,.14)",
        shadow: "0 18px 50px rgba(20,30,60,.22)",
        scrim: "rgba(18,26,45,.42)",
      };
}

// 공통 바텀시트(화면 아래에서 올라오는 시트) 오버레이.
export function InstallSheet({ children, dark, onClose }: { children: React.ReactNode; dark: boolean; onClose: () => void }) {
  const P = palette(dark);
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: P.scrim,
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        animation: "teduFadeIn .2s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          margin: "0 12px calc(12px + env(safe-area-inset-bottom))",
          borderRadius: 22,
          background: P.bg,
          border: `1px solid ${P.border}`,
          boxShadow: P.shadow,
          padding: "28px 22px 22px",
          color: P.text,
          textAlign: "center",
          // 가로로 눕힌 폰(높이 260~290)에선 카드가 화면보다 커 윗부분이 잘렸다 — 넘치면 카드 안에서 굴린다(2026-10-08).
          maxHeight: "calc(100% - 24px - env(safe-area-inset-bottom))",
          overflowY: "auto",
          boxSizing: "border-box",
          animation: "teduSlideUp .28s cubic-bezier(.2,.8,.2,1)",
        }}
      >
        {children}
      </div>

      <style>{`
        @keyframes teduFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes teduSlideUp { from { transform: translateY(24px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        @keyframes teduArrow { 0%,100% { transform: translateY(0) } 50% { transform: translateY(7px) } }
      `}</style>
    </div>
  );
}

function AppIcon() {
  return (
    <img
      src="/icon-512.png"
      alt="easy-tedu"
      width={72}
      height={72}
      style={{ width: 72, height: 72, borderRadius: 16, margin: "0 auto 14px", display: "block" }}
    />
  );
}

export function Card({ onAdd, onClose, dark }: { onAdd: () => void; onClose: () => void; dark: boolean }) {
  const t = useTranslations("components.iosGuide");
  const P = palette(dark);
  return (
    <>
      <AppIcon />
      <div style={{ fontSize: 19, fontWeight: 700, marginBottom: 6 }}>{t("cardTitle")}</div>
      <p style={{ fontSize: 14, lineHeight: 1.5, color: P.sub, margin: "0 0 20px" }}>
        {t.rich("cardDesc", { br: () => <br /> })}
      </p>
      <button
        onClick={onAdd}
        style={{
          width: "100%",
          height: 50,
          borderRadius: 14,
          border: "none",
          background: "#0A84FF",
          color: "#fff",
          fontSize: 16,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {t("cardAdd")}
      </button>
      <button
        onClick={onClose}
        style={{
          marginTop: 12,
          background: "none",
          border: "none",
          color: P.dim,
          fontSize: 13,
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {t("cardDismiss")}
      </button>
    </>
  );
}

// Safari 공유 시트는 우리가 못 그림 → 단계만 안내
// shareInMore: iOS 26+ iPhone 은 공유가 더보기(···) 안에 있어 "··· → 공유 → 홈 화면에 추가" 로 한 단계 더.
export function Steps({ onClose, shareInMore, dark }: { onClose: () => void; shareInMore: boolean; dark: boolean }) {
  const t = useTranslations("components.iosGuide");
  const P = palette(dark);
  return (
    <>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 18 }}>{t("stepsTitle")}</div>

      <ol style={{ listStyle: "none", padding: 0, margin: "0 0 8px", textAlign: "left" }}>
        {shareInMore ? (
          <>
            <Step n={1}>
              {t.rich("stepMore1", { more: () => <MoreGlyph />, b: (c) => <b>{c}</b> })}
            </Step>
            <Step n={2}>
              {t.rich("stepMore2", { share: () => <ShareGlyph />, b: (c) => <b>{c}</b> })}
            </Step>
            <Step n={3}>
              {t.rich("stepMore3", { plus: () => <PlusGlyph />, b: (c) => <b>{c}</b> })}
            </Step>
            <Step n={4}>
              {t.rich("stepAdd", { blue: (c) => <b style={{ color: "#0A84FF" }}>{c}</b> })}
            </Step>
          </>
        ) : (
          <>
            <Step n={1}>
              {t.rich("stepShare1", { share: () => <ShareGlyph />, b: (c) => <b>{c}</b> })}
            </Step>
            <Step n={2}>
              {t.rich("stepShare2", { plus: () => <PlusGlyph />, b: (c) => <b>{c}</b> })}
            </Step>
            <Step n={3}>
              {t.rich("stepAdd", { blue: (c) => <b style={{ color: "#0A84FF" }}>{c}</b> })}
            </Step>
          </>
        )}
      </ol>

      {/* 버튼 위치를 가리키는 화살표 — 26+ 는 오른쪽 아래 ···, 그 외는 하단 공유 */}
      <div
        style={{
          display: "flex",
          justifyContent: shareInMore ? "flex-end" : "center",
          margin: shareInMore ? "6px 8px 2px" : "6px 0 2px",
        }}
      >
        <div style={{ color: "#0A84FF", fontSize: 30, animation: "teduArrow 1s ease-in-out infinite" }}>↓</div>
      </div>
      <p style={{ fontSize: 12, color: P.dim, margin: "0 0 16px" }}>
        {shareInMore ? (
          t.rich("hintMore", { b: (c) => <b>{c}</b> })
        ) : (
          t("hintShare")
        )}
      </p>

      <button
        onClick={onClose}
        style={{
          width: "100%",
          height: 46,
          borderRadius: 14,
          border: `1px solid ${P.line}`,
          background: "transparent",
          color: P.text,
          fontSize: 15,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {t("done")}
      </button>
    </>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  const P = palette(isDarkTheme());
  return (
    <li style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0" }}>
      <span
        style={{
          flex: "0 0 26px",
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: "#0A84FF",
          color: "#fff",
          fontSize: 14,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {n}
      </span>
      <span style={{ fontSize: 14.5, lineHeight: 1.45, color: P.step }}>{children}</span>
    </li>
  );
}

// iOS 공유 아이콘 (네모+위 화살표)
function ShareGlyph() {
  return (
    <span
      style={{
        display: "inline-flex",
        verticalAlign: "-3px",
        margin: "0 1px",
        color: "#0A84FF",
      }}
      aria-hidden
    >
      <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
        <path d="M8 1.5v9.5M8 1.5 5 4.5M8 1.5l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 7.5H2.5v8h11v-8H12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

// 더보기(···) 아이콘 — iOS 26+ 사파리 하단 오른쪽 원형 점세개 버튼
function MoreGlyph() {
  return (
    <span style={{ display: "inline-flex", verticalAlign: "-3px", margin: "0 1px", color: "#0A84FF" }} aria-hidden>
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7.3" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="5.6" cy="9" r="1" fill="currentColor" />
        <circle cx="9" cy="9" r="1" fill="currentColor" />
        <circle cx="12.4" cy="9" r="1" fill="currentColor" />
      </svg>
    </span>
  );
}

// 정보(ⓘ) 아이콘 — 힌트 알약 앞에. 알약 글자색(currentColor)을 따라감.
function InfoGlyph() {
  return (
    <span style={{ display: "inline-flex", verticalAlign: "-3px" }} aria-hidden>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6.6" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="8" cy="5" r="1" fill="currentColor" />
        <path d="M8 7.4v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </span>
  );
}

// 홈 화면에 추가 아이콘 (네모+플러스)
function PlusGlyph() {
  return (
    <span style={{ display: "inline-flex", verticalAlign: "-3px", margin: "0 1px", color: "#0A84FF" }} aria-hidden>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="1.5" y="1.5" width="13" height="13" rx="3" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 5v6M5 8h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </span>
  );
}

// ── 안드로이드(갤럭시 등) — 크롬·삼성 인터넷은 프로그램으로 설치를 띄울 수 있다(beforeinstallprompt).
//    못 잡았으면(아직 안 떴거나 브라우저가 안 줌) 메뉴 단계 안내로 대신한다. 인앱·이미 설치됨은 제외.
export function detectAndroidInstall(): { eligible: boolean; samsung: boolean } {
  if (typeof window === "undefined") return { eligible: false, samsung: false };
  const ua = window.navigator.userAgent || "";
  if (!/android/i.test(ua)) return { eligible: false, samsung: false };
  if (window.matchMedia("(display-mode: standalone)").matches) return { eligible: false, samsung: false };
  const inApp = /kakaotalk|fban|fbav|instagram|line\/|naver|daumapps|everytimeapp|; wv\)/i.test(ua); // "; wv)" = 안드로이드 WebView
  if (inApp) return { eligible: false, samsung: false };
  return { eligible: true, samsung: /SamsungBrowser/i.test(ua) };
}

// 안드로이드 수동 단계 — 크롬(⋮ → 홈 화면에 추가) / 삼성 인터넷(≡ → 현재 페이지 추가 → 홈 화면)
export function AndroidSteps({ onClose, samsung, dark }: { onClose: () => void; samsung: boolean; dark: boolean }) {
  const t = useTranslations("components.iosGuide");
  const P = palette(dark);
  return (
    <>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 18 }}>{t("stepsTitle")}</div>
      <ol style={{ listStyle: "none", padding: 0, margin: "0 0 8px", textAlign: "left" }}>
        {samsung ? (
          <>
            <Step n={1}>{t.rich("stepSamsung1", { b: (c) => <b>{c}</b> })}</Step>
            <Step n={2}>{t.rich("stepSamsung2", { b: (c) => <b>{c}</b> })}</Step>
            <Step n={3}>{t.rich("stepAndroidAdd", { blue: (c) => <b style={{ color: "#0A84FF" }}>{c}</b> })}</Step>
          </>
        ) : (
          <>
            <Step n={1}>{t.rich("stepChrome1", { b: (c) => <b>{c}</b> })}</Step>
            <Step n={2}>{t.rich("stepChrome2", { b: (c) => <b>{c}</b> })}</Step>
            <Step n={3}>{t.rich("stepAndroidAdd", { blue: (c) => <b style={{ color: "#0A84FF" }}>{c}</b> })}</Step>
          </>
        )}
      </ol>
      <div style={{ display: "flex", justifyContent: samsung ? "flex-end" : "flex-end", margin: "6px 8px 2px" }}>
        <div style={{ color: "#0A84FF", fontSize: 30, animation: samsung ? "teduArrow 1s ease-in-out infinite" : "none", transform: samsung ? undefined : "rotate(180deg)" }}>↓</div>
      </div>
      <p style={{ fontSize: 12, color: P.dim, margin: "0 0 16px" }}>{samsung ? t("hintSamsung") : t("hintChrome")}</p>
      <button
        onClick={onClose}
        style={{ width: "100%", height: 46, borderRadius: 14, border: `1px solid ${P.line}`, background: "transparent", color: P.text, fontSize: 15, fontWeight: 600, cursor: "pointer" }}
      >
        {t("done")}
      </button>
    </>
  );
}
